// packages/api/src/hooks/useMyAddresses.ts
// Source: docs/contracts/ESCR-profile-selfservice.md

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ApiSuccess,
  CreateProfileAddressInput,
  ProfileAddress,
  UpdateProfileAddressInput,
} from '@alamo/types';

import { apiClient } from '../client';
import { queryKeys } from '../query-keys';

export function useMyAddresses() {
  return useQuery({
    queryKey: queryKeys.profile.addresses(),
    queryFn: async (): Promise<ProfileAddress[]> => {
      const response = await apiClient.get<ApiSuccess<ProfileAddress[]>>('/profile/addresses');
      return response.data.data;
    },
  });
}

export function useCreateMyAddress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateProfileAddressInput): Promise<ProfileAddress> => {
      const response = await apiClient.post<ApiSuccess<ProfileAddress>>(
        '/profile/addresses',
        input,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.addresses() });
      // Corrigir endereço pode limpar a rejeição do bloco no fluxo de correção.
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.myRegistration() });
    },
  });
}

export function useUpdateMyAddress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: UpdateProfileAddressInput;
    }): Promise<ProfileAddress> => {
      const response = await apiClient.patch<ApiSuccess<ProfileAddress>>(
        `/profile/addresses/${id}`,
        input,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.addresses() });
      // Corrigir endereço pode limpar a rejeição do bloco no fluxo de correção.
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.myRegistration() });
    },
  });
}

export function useDeleteMyAddress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/profile/addresses/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.addresses() });
      // Corrigir endereço pode limpar a rejeição do bloco no fluxo de correção.
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.myRegistration() });
    },
  });
}
